import './App.css';
import {BrowserRouter, Link, Route, Routes, Navigate} from 'react-router-dom';
import {ThemeProvider} from '@mui/material/styles';
import { theme } from './theme/theme.ts';
import CssBaseline from '@mui/material/CssBaseline';
import React, {useEffect, useMemo, useState} from 'react';

import UserPage from "./components/User.tsx";
import OrganizationManagement from "./components/OrganizationManagement.tsx";
import { OrganizationSwitcher } from "./components/OrganizationSwitcher.tsx";
import ModuleSwitcher from "./components/ModuleSwitcher.tsx";
import { ChatSidebar } from "./components/ChatSidebar.tsx";
import { getKeycloakInstance, initKeycloak } from "./keycloak.ts";
import { setToken } from "./store/auth/authSlice.ts";
import {useDispatch, useSelector} from "react-redux";
import { selectUserName } from "./store/user/userSlice.ts";
import { selectCurrentOrganization, selectOrganizationsLoaded } from "./store/organization/organizationSlice.ts";

import Keycloak from 'keycloak-js';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Fab from '@mui/material/Fab';
import {stringAvatar} from "./utils/avatar.ts";
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ChatIcon from '@mui/icons-material/Chat';
import { getAvailableModules, getModuleById, MODULES, MODULE_STORAGE_KEY } from './modules/moduleRegistry.tsx';

const MODULE_ROUTES = MODULES.flatMap(module => module.routes);

const App = () => {
    const userName = useSelector(selectUserName);
    const selectedOrganization = useSelector(selectCurrentOrganization);
    const organizationsLoaded = useSelector(selectOrganizationsLoaded);

    const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
    const [authenticated, setAuthenticated] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [chatOpen, setChatOpen] = useState(false);
    const dispatch = useDispatch();

    const open = Boolean(anchorEl);

    const availableModules = useMemo(
        () => getAvailableModules(selectedOrganization),
        [selectedOrganization],
    );
    const [activeModuleId, setActiveModuleId] = useState<string | null>(null);

    useEffect(() => {
        // Wait until organizations are loaded to avoid clearing module selection too early
        if (!organizationsLoaded) {
            return;
        }

        if (!selectedOrganization) {
            setActiveModuleId(null);
            return;
        }

        const storedModule = typeof window !== 'undefined'
            ? localStorage.getItem(MODULE_STORAGE_KEY)
            : null;

        if (storedModule && availableModules.some(module => module.id === storedModule)) {
            setActiveModuleId(storedModule);
            return;
        }

        const fallbackModule = availableModules[0]?.id ?? null;
        setActiveModuleId(fallbackModule);

        if (typeof window !== 'undefined') {
            if (fallbackModule) {
                localStorage.setItem(MODULE_STORAGE_KEY, fallbackModule);
            } else {
                localStorage.removeItem(MODULE_STORAGE_KEY);
            }
        }
    }, [availableModules, organizationsLoaded, selectedOrganization]);

    const activeModule = useMemo(() => getModuleById(activeModuleId), [activeModuleId]);
    const moduleNavItems = activeModule?.navItems ?? [];

    useEffect(() => {
        initKeycloak()
            .then(() => {
                const kc = getKeycloakInstance();
                setKeycloak(kc);
                setAuthenticated(kc.authenticated || false);

                // Set token in Redux after authentication
                if (kc.authenticated && kc.token) {
                    dispatch(setToken(kc.token));
                }
            })
            .catch((error) => console.error('Keycloak initialization failed', error));
    }, [dispatch]);

    const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleClose();
        keycloak?.logout();
    };

    if (!keycloak) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Typography>Loading...</Typography>
        </Box>;
    }

    if (!authenticated) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Alert severity="error">Unable to authenticate</Alert>
        </Box>;
    }


    const hasOrganization = selectedOrganization !== null;
    const orgsLoading = !organizationsLoaded;

    const orgGuard = (element: React.ReactNode): React.ReactElement => {
        if (orgsLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Typography>Loading organization...</Typography>
                </Box>
            );
        }

        return hasOrganization ? <>{element}</> : <Navigate to="/" replace />;
    };

    const landingElement = hasOrganization && moduleNavItems[0]?.path
        ? <Navigate to={moduleNavItems[0].path} replace />
        : <UserPage />;

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter basename="/">
                <AppBar position="static">
                    <Toolbar>
                        {userName && (
                            <>
                                <IconButton
                                    onClick={handleAvatarClick}
                                    sx={{ p: 0, mr: 2 }}
                                >
                                    <Avatar {...stringAvatar(userName)} />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    onClick={handleClose}
                                >
                                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                                </Menu>
                            </>
                        )}
                        <OrganizationSwitcher />
                        <ModuleSwitcher
                            activeModuleId={activeModuleId}
                            onModuleChange={(moduleId) => setActiveModuleId(moduleId)}
                        />
                        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            Robust Risk
                        </Typography>
                        {hasOrganization && moduleNavItems.map(navItem => {
                            const IconComponent = navItem.icon;
                            return (
                                <Button
                                    key={navItem.path}
                                    color="inherit"
                                    component={Link}
                                    to={navItem.path}
                                    startIcon={<IconComponent />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {navItem.label}
                                </Button>
                            );
                        })}
                        {/* Organization link should be visible to admin/owner regardless of module enablement */}
                        {hasOrganization && (selectedOrganization?.role === 'admin' || selectedOrganization?.role === 'owner') && (
                            <Button color="inherit" component={Link} to="/organization" startIcon={<BusinessIcon />} sx={{ textTransform: 'none' }}>
                                Organization
                            </Button>
                        )}
                        <Button color="inherit" component={Link} to="/user" startIcon={<PersonIcon />} sx={{ textTransform: 'none', ml: 1 }}>
                            Profile
                        </Button>
                        {/* 'User' menu removed — home shows the same information */}
                    </Toolbar>
                </AppBar>
                <Container maxWidth="xl">
                    <Box sx={{ mt: 4 }}>
                        {!hasOrganization && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                You need to create or join an organization to access risks, mitigations, and actors.
                            </Alert>
                        )}
                        <Routes>
                            <Route path="/" element={landingElement} />
                            {MODULE_ROUTES.map(route => (
                                <Route
                                    key={route.path}
                                    path={route.path}
                                    element={orgGuard(route.element)} />
                            ))}
                            <Route path="/organization" element={orgGuard(<OrganizationManagement />)} />
                            <Route path="/user" element={<UserPage />} />
                        </Routes>
                    </Box>
                </Container>

                {/* Floating Chat Button */}
                {hasOrganization && (
                    <Fab
                        color="primary"
                        aria-label="chat"
                        onClick={() => setChatOpen(true)}
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            right: 16,
                        }}
                    >
                        <ChatIcon />
                    </Fab>
                )}

                {/* Chat Sidebar */}
                <ChatSidebar open={chatOpen} onClose={() => setChatOpen(false)} />
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
